import React, { useEffect, useRef, useState } from "react";
import { X, Wifi, MapPin } from "lucide-react";
import { api } from "@/lib/api";

/**
 * FlashBadge — full-screen takeover that instantly displays the crew's PJP
 * Digital ID for an FBO / ramp / hangar operator to visually verify or scan.
 *
 * UX rules baked in:
 *   • Locks screen to landscape/portrait orientation and requests a wake-lock
 *     so the phone doesn't dim mid-scan.
 *   • Boosts brightness on iOS Safari via a white-flash frame (best we can
 *     do without native APIs — the enhanced contrast helps camera scanners).
 *   • Big pulsing green LED (top-right) signals "LIVE — this badge is real".
 *   • Auto-dismisses after `timeoutMs` (default 90s) so the phone locks itself.
 *   • Any tap outside the actual badge frame closes early.
 *   • Optional short haptic buzz on open (browsers that support navigator.vibrate).
 *
 * Renders the same `/id/{token}?embed=1` badge page the rest of the app uses,
 * so there is exactly ONE canonical badge representation.
 */
export default function FlashBadge({ open, token, onClose, timeoutMs = 90_000 }) {
  const [remaining, setRemaining] = useState(Math.floor(timeoutMs / 1000));
  const [airport, setAirport] = useState(null);   // { icao, city, distance_nm } from /api/id/flash
  const wakeLockRef = useRef(null);
  const startedAtRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    startedAtRef.current = Date.now();
    setRemaining(Math.floor(timeoutMs / 1000));
    setAirport(null);

    // Short haptic acknowledgment on supported browsers (Android Chrome).
    try { navigator.vibrate?.(60); } catch {}

    // Request screen wake-lock. Silently swallow failures (older Safari, etc.).
    (async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch {}
    })();

    // ==== Audit ping — best-effort ====
    // 1) Fire an immediate log without geo (guaranteed record even if the user
    //    denies location permission or is on desktop with no GPS).
    // 2) Try to get a coarse geolocation fix (30s timeout) and, if we get one,
    //    log a second event with lat/lng so we can resolve the airport.
    let cancelled = false;
    const logFlash = async (coords) => {
      try {
        const body = coords
          ? { latitude: coords.latitude, longitude: coords.longitude, accuracy_m: coords.accuracy }
          : {};
        const r = await api.post("/id/flash", body);
        if (!cancelled && r?.data?.airport) setAirport(r.data.airport);
      } catch {}
    };
    logFlash(null);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { if (!cancelled) logFlash(pos.coords); },
        () => {},
        { enableHighAccuracy: false, timeout: 30_000, maximumAge: 60_000 }
      );
    }

    // Countdown tick — auto-close when it runs out.
    const int = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      const left = Math.max(0, Math.floor((timeoutMs - elapsed) / 1000));
      setRemaining(left);
      if (left <= 0) {
        clearInterval(int);
        onClose?.();
      }
    }, 500);

    // ESC to close.
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);

    return () => {
      cancelled = true;
      clearInterval(int);
      window.removeEventListener("keydown", onKey);
      if (wakeLockRef.current) {
        try { wakeLockRef.current.release(); } catch {}
        wakeLockRef.current = null;
      }
    };
  }, [open, timeoutMs, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="flash-badge-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[999] flex flex-col items-center justify-center p-4"
      style={{
        // Bright near-white background helps ID scanners in low light while
        // keeping the badge itself against a dark frame.
        background: "radial-gradient(ellipse at center, #F5F7FB 0%, #E7ECF3 60%, #D5DBE3 100%)",
      }}
    >
      {/* Top status strip */}
      <div className="w-full max-w-md flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-slate-700 font-semibold">// PJP ID · LIVE</div>
          {airport && (
            <span
              data-testid="flash-badge-airport-chip"
              className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-cyan/15 text-cyan border border-cyan/40 flex items-center gap-1 ml-1"
            >
              <MapPin className="h-2.5 w-2.5" /> {airport.icao} · {airport.distance_nm}nm
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="mono text-[10px] tracking-widest text-slate-500 flex items-center gap-1">
            <Wifi className="h-3 w-3" /> AUTO-CLOSE {remaining}s
          </div>
          <button
            type="button"
            data-testid="flash-badge-close"
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            className="p-1.5 rounded-full bg-white/80 border border-slate-300 text-slate-600 hover:bg-white hover:text-slate-900 shadow-sm"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Badge frame */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-300 bg-obsidian"
        style={{
          width: "min(90vw, 420px)",
          aspectRatio: "1 / 2",
          maxHeight: "82vh",
        }}
      >
        {token ? (
          <iframe
            data-testid="flash-badge-iframe"
            src={`/id/${token}?embed=1&flash=1`}
            title="PJP Digital ID"
            className="block w-full h-full"
            style={{ border: 0, background: "transparent" }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">
            Badge not provisioned. Open Digital ID once to activate.
          </div>
        )}
      </div>

      {/* Bottom instruction */}
      <div className="w-full max-w-md text-center mt-4">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-slate-600">
          Show this screen · Scan QR to verify
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          Tap anywhere to dismiss. Auto-closes in {remaining}s to save battery.
        </div>
      </div>
    </div>
  );
}
